import type { AreaCode } from "./areaUtils";

/**
 * Best-effort mapping from a (lat, lng) to a Norwegian price area.
 *
 * Static lat/lng thresholds — coarse but stable. Price-area borders follow
 * grid topology, not strict lat/lng lines, so the fine boundary is fuzzy
 * for ~1–2 % of points near the seams (e.g. inner Sogn / Hardangervidda
 * for NO5 vs NO1, far-south Trøndelag for NO3 vs NO1). The user can
 * correct via the area selector chip if the guess feels wrong.
 *
 * Returns `null` for coordinates outside a generous Norway bounding box —
 * lets callers show a "couldn't place you" message instead of guessing.
 */
export const areaFromLatLng = (lat: number, lng: number): AreaCode | null => {
	// Quick out: outside Norway-ish bounds.
	if (lat < 57 || lat > 72 || lng < 4 || lng > 32) return null;

	// North-to-south latitude bands. NO4 sits above the Helgeland latitude
	// (around 65°), NO3 spans Trøndelag + Møre og Romsdal down to ~62°.
	if (lat >= 65) return "NO4";
	if (lat >= 62) return "NO3";

	// Below 62°, lng decides: NO5 (Vestland) is west of ~7.5°,
	// NO1 (Østlandet) is east. NO2 (Sørlandet / Rogaland) takes the
	// southern coastal band below ~59.5°.
	if (lat < 59.5) return "NO2";
	if (lng < 7.5) return "NO5";
	return "NO1";
};
